<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Bulk Change Campaigns
    </h2>
    <Card class="mb-3">
      <template #content>
        <div class="grid">
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="bulk_mode">Type of bulk change</label>
            <MultiSelect
              input-id="bulk_mode"
              v-model="bulk_mode"
              :options="availableBulkModes"
              optionLabel="label"
              optionValue="value"
              placeholder="Select bulk change mode(s)"
              :class="{
                'p-invalid': submitted && (!bulk_mode || !bulk_mode.length),
              }"
              @change="handleBulkModeSwitch"
              display="chip"
            />
            <small
              v-if="submitted && (!bulk_mode || !bulk_mode.length)"
              class="p-error"
            >
              Please select at least one bulk change mode
            </small>
          </div>
          <div
            v-if="isNetworkMode"
            class="col-12 md:col-6 flex flex-column gap-1"
          >
            <label for="network_campaigns">Increase Interval for all:</label>
            <Dropdown
              input-id="network_campaigns"
              v-model="usOrNoSelected"
              :options="us_or_non_us"
              optionLabel="label"
              optionValue="value"
              placeholder="Select a type of campaign"
              :class="{ 'p-invalid': submitted && !usOrNoSelected }"
            />
            <small v-if="submitted && !usOrNoSelected" class="p-error">
              Please select a type of campaign
            </small>
          </div>
          <div
            v-if="isSpotifyOrPromotionMode"
            class="col-12 md:col-6 flex flex-column gap-1"
          >
            <label for="select_hosted">Select Hosting Platforms:</label>
            <MultiSelect
              input-id="select_hosted"
              v-model="selectedHosted"
              :options="hostedOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select hosting platforms"
              display="chip"
              filter
              :filter-fields="['label']"
              @change="handleHostedChange"
            />
          </div>
          <div
            v-if="usOrNoSelected || isSpotifyOrPromotionMode"
            class="col-12 md:col-6 flex flex-column gap-1"
          >
            <label for="select_podcasters">Select Podcasters (optional):</label>
            <MultiSelect
              input-id="select_podcasters"
              v-model="selectedPodcasters"
              :options="podcastersOptions"
              optionLabel="name"
              optionValue="podcaster_id"
              placeholder="Select podcasters"
              display="chip"
              auto-filter-focus
              filter
              :filter-fields="['name']"
              @change="handlePodcasterChange"
            />
          </div>
          <div
            v-if="usOrNoSelected"
            class="col-12 md:col-6 flex flex-column gap-1"
          >
            <label for="select_shows">Exclude Shows:</label>
            <MultiSelect
              input-id="select_shows"
              v-model="excludeShows"
              :options="filteredExcludeShows"
              optionLabel="show_title"
              optionValue="show_id"
              placeholder="Exclude shows"
              display="chip"
              auto-filter-focus
              filter
              :filter-fields="['show_title']"
              :panelStyle="{ maxWidth: 'min(500px, 45vw)' }"
            />
          </div>
          <div
            v-if="isSpotifyOrPromotionMode"
            class="col-12 md:col-6 flex flex-column gap-1"
          >
            <label for="select_shows">Select Shows:</label>
            <MultiSelect
              input-id="select_shows"
              v-model="selectedShows"
              :options="filteredShows"
              optionLabel="show_title"
              optionValue="show_id"
              placeholder="Select shows"
              display="chip"
              auto-filter-focus
              filter
              :filter-fields="['show_title']"
              :class="{ 'p-invalid': submitted && !selectedShows.length }"
              :panelStyle="{ maxWidth: 'min(500px, 45vw)' }"
            />
            <small v-if="submitted && !selectedShows.length" class="p-error">
              Please select at least one show
            </small>
          </div>
          <div
            v-if="selectedShows?.length || usOrNoSelected"
            class="col-12 md:col-6 flex flex-column gap-1"
          >
            <label for="interval_policy_type"
              >Promotions Per Interval Mode</label
            >
            <Dropdown
              input-id="interval_policy_type"
              v-model="intervalPolicyType"
              :options="intervalPolicyOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select mode"
              :class="{ 'p-invalid': submitted && !intervalPolicyType }"
            />
            <small v-if="submitted && !intervalPolicyType" class="p-error">
              Please select a mode
            </small>
          </div>
          <div
            v-if="
              (selectedShows?.length || usOrNoSelected) &&
              intervalPolicyType === 'percentage'
            "
            class="col-12 md:col-6 flex flex-column gap-1"
          >
            <label for="percentage"
              >Increase{{ isNetworkMode ? "" : "/Decrease" }} Interval by
              percentage:</label
            >
            <InputGroup>
              <SelectButton
                v-if="!isNetworkMode"
                v-model="increaseOrDecrease"
                :options="increaseOrDecreaseOptions"
                option-label="label"
                option-value="value"
              />
              <InputNumber
                input-id="percentage"
                :max-fraction-digits="2"
                prefix="%"
                v-model="percentage"
                placeholder="Enter percentage number"
                :min="0"
              />
            </InputGroup>
            <small
              v-if="
                submitted &&
                (!sameForAllPercent ||
                  sameForAllPercent === 0 ||
                  sameForAllPercent < -100)
              "
              class="p-error"
            >
              Please enter a valid percentage
            </small>
          </div>
          <div
            v-if="
              (selectedShows?.length || usOrNoSelected) &&
              intervalPolicyType === 'random'
            "
            class="col-12 md:col-3 flex flex-column gap-1"
          >
            <label for="min_interval">Min Interval</label>
            <InputNumber
              input-id="min_interval"
              v-model="minInterval"
              placeholder="Enter min interval"
              :min="0.1"
              :step="0.1"
              :minFractionDigits="1"
              :maxFractionDigits="1"
              :class="{
                'p-invalid':
                  submitted && (!minInterval || minInterval >= maxInterval),
              }"
            />
            <small
              v-if="submitted && (!minInterval || minInterval >= maxInterval)"
              class="p-error"
            >
              Please enter a valid min interval less than max
            </small>
          </div>
          <div
            v-if="
              (selectedShows?.length || usOrNoSelected) &&
              intervalPolicyType === 'random'
            "
            class="col-12 md:col-3 flex flex-column gap-1"
          >
            <label for="max_interval">Max Interval</label>
            <InputNumber
              input-id="max_interval"
              v-model="maxInterval"
              placeholder="Enter max interval"
              :min="0.1"
              :step="0.1"
              :minFractionDigits="1"
              :maxFractionDigits="1"
              :class="{
                'p-invalid':
                  submitted && (!maxInterval || maxInterval <= minInterval),
              }"
            />
            <small
              v-if="submitted && (!maxInterval || maxInterval <= minInterval)"
              class="p-error"
            >
              Please enter a valid max interval greater than min
            </small>
          </div>
        </div>
      </template>
    </Card>
    <div class="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        @click="router.go(-1)"
        outlined
        class="p-button-danger"
      />
      <Button
        label="Submit"
        @click="submitBulkChange"
        class="p-button-success"
      />
    </div>
  </div>
</template>

<script setup>
import Card from "primevue/card";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import { onBeforeMount, ref } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import InputNumber from "primevue/inputnumber";
import { computed } from "vue";
import Button from "primevue/button";
import { API_BASE_URL } from "@/consts";
import { useToast } from "primevue/usetoast";
import { useRouter } from "vue-router";
import InputGroup from "primevue/inputgroup";
import SelectButton from "primevue/selectbutton";

const toast = useToast();

const router = useRouter();

const store = useStore();

const token = localStorage.getItem("podcastAdminToken");

const bulk_mode = ref([]);

const usOrNoSelected = ref(null);

const selectedShows = ref([]);

const excludeShows = ref([]);

const selectedHosted = ref([]);

const selectedPodcasters = ref([]);

const percentage = ref(0);

const sameForAllPercent = ref(0);

const submitted = ref(false);

const increaseOrDecrease = ref(1);

const increaseOrDecreaseOptions = [
  { label: "Increase", value: 1 },
  { label: "Decrease", value: -1 },
];

const intervalPolicyType = ref("percentage");

const intervalPolicyOptions = [
  { label: "Same change for all", value: "percentage" },
  { label: "Random Interval", value: "random" },
];

const minInterval = ref(1);

const maxInterval = ref(2);

const bulk_modes = [
  {
    label: "Intervals of Network Campaigns",
    value: "network_campaigns_intervals",
  },
  {
    label: "Intervals of Spotify Campaigns",
    value: "spotify_campaigns_intervals",
  },
  {
    label: "Intervals of Promotion Campaigns",
    value: "promotion_campaigns_intervals",
  },
];

const us_or_non_us = [
  {
    label: "US Campaigns under Network",
    value: "us_campaigns",
  },
  {
    label: "Non-US Campaigns under Network",
    value: "non_us_campaigns",
  },
];

const isNetworkMode = computed(() => {
  return bulk_mode.value.includes("network_campaigns_intervals");
});

const isSpotifyOrPromotionMode = computed(() =>
  ["spotify_campaigns_intervals", "promotion_campaigns_intervals"].some(
    (mode) => bulk_mode.value.includes(mode)
  )
);

const availableBulkModes = computed(() => {
  if (isNetworkMode.value) {
    return bulk_modes.filter(
      (mode) => mode.value === "network_campaigns_intervals"
    );
  } else if (isSpotifyOrPromotionMode.value) {
    return bulk_modes.filter((mode) =>
      ["spotify_campaigns_intervals", "promotion_campaigns_intervals"].includes(
        mode.value
      )
    );
  }
  return bulk_modes;
});

const shows = computed(() => {
  if (!selectedHosted.value.length) {
    return store.getters.shows;
  }

  return store.getters.shows.filter((show) =>
    selectedHosted.value.includes(show.hosted)
  );
});

const hostedOptions = computed(() => {
  const hostedValues = new Set();
  store.getters.shows.forEach((show) => {
    if (show.hosted) {
      hostedValues.add(show.hosted);
    }
  });
  return Array.from(hostedValues)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ label: value, value }));
});

const podcastersOptions = computed(() => {
  const podcastersMap = new Map();
  store.getters.shows.forEach((show) => {
    if (show.podcaster_id && show.name) {
      podcastersMap.set(show.podcaster_id, {
        podcaster_id: show.podcaster_id,
        name: show.name,
      });
    }
  });
  return Array.from(podcastersMap.values()).sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );
});

const filteredShows = computed(() => {
  let showsList = shows.value;

  if (selectedPodcasters.value.length > 0) {
    showsList = showsList.filter((show) =>
      selectedPodcasters.value.includes(show.podcaster_id)
    );
  }

  if (isSpotifyOrPromotionMode.value) {
    const selectedCampaignTypes = [];
    if (bulk_mode.value.includes("spotify_campaigns_intervals")) {
      selectedCampaignTypes.push("spotify");
    }
    if (bulk_mode.value.includes("promotion_campaigns_intervals")) {
      selectedCampaignTypes.push("promotion");
    }

    showsList = showsList.filter((show) =>
      store.getters.campaigns.some(
        (campaign) =>
          campaign.show_id === show.show_id &&
          selectedCampaignTypes.includes(campaign.campaign_type)
      )
    );
  }

  return showsList;
});

const filteredExcludeShows = computed(() => {
  let showsList = store.getters.shows;

  if (selectedPodcasters.value.length > 0) {
    showsList = showsList.filter((show) =>
      selectedPodcasters.value.includes(show.podcaster_id)
    );
  }

  return showsList;
});

const selectedCampaigns = computed(() => {
  let campaignIds = [];

  if (isNetworkMode.value) {
    const networkCampaigns = store.getters.campaigns
      .filter(
        (campaign) =>
          (usOrNoSelected.value === "us_campaigns"
            ? campaign.geos_wl?.includes("us")
            : !campaign.geos_wl?.includes("us")) &&
          !excludeShows.value.includes(campaign.show_id) &&
          (selectedPodcasters.value.length === 0 ||
            selectedPodcasters.value.includes(campaign.podcaster_id))
      )
      .map((campaign) => campaign.campaign_id);

    campaignIds = [...campaignIds, ...networkCampaigns];
  }

  if (isSpotifyOrPromotionMode.value) {
    const spotifyPromotionCampaigns = store.getters.campaigns
      .filter(
        (campaign) =>
          selectedShows.value.includes(campaign.show_id) &&
          ((bulk_mode.value.includes("spotify_campaigns_intervals") &&
            campaign.campaign_type === "spotify") ||
            (bulk_mode.value.includes("promotion_campaigns_intervals") &&
              campaign.campaign_type === "promotion"))
      )
      .map((campaign) => campaign.campaign_id);

    campaignIds = [...campaignIds, ...spotifyPromotionCampaigns];
  }

  return [...new Set(campaignIds)];
});

const handleBulkModeSwitch = () => {
  usOrNoSelected.value = null;
  selectedShows.value = [];
  selectedPodcasters.value = [];
  percentage.value = 0;
  selectedHosted.value = [];
  excludeShows.value = [];
  intervalPolicyType.value = "percentage";
  minInterval.value = 1;
  maxInterval.value = 2;
};

// Reset selected/excluded shows when podcaster selection changes
const handlePodcasterChange = () => {
  selectedShows.value = [];
  excludeShows.value = [];
};

const handleHostedChange = () => {
  selectedShows.value = [];
};

const fetchCampaigns = async () => {
  if (!store.getters.campaigns || store.getters.campaigns.length == 0) {
    store.dispatch("loadCampaigns", { token });
  }
};

const fetchShows = () => {
  if (!store.getters.shows || store.getters.shows.length == 0) {
    store.dispatch("loadShows", { token });
  }
};

const submitBulkChange = async () => {
  submitted.value = true;
  sameForAllPercent.value = increaseOrDecrease.value * percentage.value;

  try {
    if (!bulk_mode.value || !bulk_mode.value.length) return;
    if (!intervalPolicyType.value) return;
    if (
      intervalPolicyType.value === "percentage" &&
      (sameForAllPercent.value < -100 || sameForAllPercent.value === 0)
    )
      return;
    if (
      intervalPolicyType.value === "random" &&
      (minInterval.value >= maxInterval.value ||
        !minInterval.value ||
        !maxInterval.value)
    )
      return;
    if (isNetworkMode.value && !usOrNoSelected.value) return;
    if (isSpotifyOrPromotionMode.value && !selectedShows.value.length) return;

    const intervalPolicy = {
      type: intervalPolicyType.value,
    };
    if (intervalPolicy.type === "percentage") {
      intervalPolicy.percentage = sameForAllPercent.value;
    } else {
      intervalPolicy.min = minInterval.value;
      intervalPolicy.max = maxInterval.value;
    }

    store.commit("setLoading", true);

    const response = await axios.post(
      `${API_BASE_URL}/campaigns/bulk-change`,
      {
        intervalPolicy,
        campaignIds: selectedCampaigns.value,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data.success) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: response.data.data.message,
        life: 6000,
      });
      router.go(-1);
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: response.data.message,
        life: 4000,
      });
    }
  } catch (error) {
    console.error(error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "An error occurred while processing your request",
      life: 4000,
    });
  } finally {
    store.commit("setLoading", false);
  }
};

onBeforeMount(() => {
  fetchCampaigns();
  fetchShows();
});
</script>
